import { formatStatusLabel, getStatusChipColor } from '@/ui/primitives';
import type {
  ExecutionStatusKey,
  MetricsDto,
  MetricsSeriesPoint,
} from '@/features/metrics/metricsHelpers';
import type { StackedBarChartDatum } from '@/ui/primitives/StackedBarChart';

type StackedExecutionSeries = {
  key: ExecutionStatusKey;
  label: string;
  colorKey: ReturnType<typeof getStatusChipColor>;
};

type StackedExecutionChartData = {
  data: StackedBarChartDatum[];
  series: StackedExecutionSeries[];
};

const statusOrder: ExecutionStatusKey[] = ['passed', 'failed', 'errored', 'skipped', 'untested'];

const normalizeDateLabel = (label: string): string | null => {
  const trimmed = label.trim();
  if (trimmed.length === 10 && trimmed.includes('-')) {
    return trimmed.slice(0, 10);
  }
  if (trimmed.length === 10 && trimmed.includes('/')) {
    const [day, month, year] = trimmed.split('/');
    if (day && month && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
  }
  return null;
};

const buildSeriesValues = (
  series: MetricsSeriesPoint[] | undefined,
  labels: string[],
  dates: string[],
): number[] => {
  const valuesByLabel = new Map<string, number | null>();
  (series ?? []).forEach((point) => {
    valuesByLabel.set(point.label, point.value);
    const normalized = normalizeDateLabel(point.label);
    if (normalized) {
      valuesByLabel.set(normalized, point.value);
    }
  });
  return labels.map((label, index) => {
    const dateKey = dates[index];
    const valueByLabel = valuesByLabel.get(label);
    if (typeof valueByLabel === 'number') return valueByLabel;
    const normalizedLabel = normalizeDateLabel(label);
    if (normalizedLabel) {
      const valueByNormalizedLabel = valuesByLabel.get(normalizedLabel);
      if (typeof valueByNormalizedLabel === 'number') return valueByNormalizedLabel;
    }
    if (dateKey) {
      const dateKeyNormalized = normalizeDateLabel(dateKey) ?? dateKey.slice(0, 10);
      const valueByDate = valuesByLabel.get(dateKey);
      if (typeof valueByDate === 'number') return valueByDate;
      const valueByDateNormalized = valuesByLabel.get(dateKeyNormalized);
      if (typeof valueByDateNormalized === 'number') return valueByDateNormalized;
    }
    if (series && series.length === labels.length) {
      const seriesValue = series[index]?.value;
      if (typeof seriesValue === 'number') return seriesValue;
    }
    return 0;
  });
};

const buildStatusCounts = (total: number, rates: number[]): number[] => {
  if (total <= 0) return rates.map(() => 0);
  const counts = rates.map((rate) => Math.round((total * rate) / 100));
  const sum = counts.reduce((acc, value) => acc + value, 0);
  if (sum !== total && counts.length > 0) {
    counts[0] = Math.max(0, counts[0] + (total - sum));
  }
  return counts;
};

export const buildStackedExecutionChartData = (
  metrics: MetricsDto | null | undefined,
): StackedExecutionChartData | null => {
  const labels = metrics?.metricsRangeLabels ?? [];
  const dates = metrics?.metricsRangeDates ?? [];
  if (labels.length === 0 || !metrics) return null;
  const totals = buildSeriesValues(metrics.series.executionTrendMetrics, labels, dates);
  const ratesByStatus: Record<ExecutionStatusKey, number[]> = {
    passed: buildSeriesValues(metrics.series.filteredPassRate, labels, dates),
    failed: buildSeriesValues(metrics.series.filteredFailureRate, labels, dates),
    errored: buildSeriesValues(metrics.series.filteredErrorRate, labels, dates),
    skipped: buildSeriesValues(metrics.series.filteredSkipRate, labels, dates),
    untested: buildSeriesValues(metrics.series.filteredUntestedRate, labels, dates),
  };
  const data: StackedBarChartDatum[] = labels.map((label, index) => {
    const total = totals[index] ?? 0;
    const counts = buildStatusCounts(
      total,
      statusOrder.map((status) => ratesByStatus[status][index] ?? 0),
    );
    return statusOrder.reduce<StackedBarChartDatum>(
      (acc, status, statusIndex) => ({
        ...acc,
        [status]: counts[statusIndex] ?? 0,
      }),
      { label },
    );
  });
  const series: StackedExecutionSeries[] = [
    {
      key: 'passed',
      label: formatStatusLabel('passed'),
      colorKey: getStatusChipColor('passed'),
    },
    {
      key: 'failed',
      label: formatStatusLabel('failed'),
      colorKey: getStatusChipColor('failed'),
    },
    {
      key: 'errored',
      label: formatStatusLabel('errored'),
      colorKey: getStatusChipColor('errored'),
    },
    {
      key: 'skipped',
      label: formatStatusLabel('skipped'),
      colorKey: getStatusChipColor('skipped'),
    },
    {
      key: 'untested',
      label: formatStatusLabel('untested'),
      colorKey: getStatusChipColor('untested'),
    },
  ];

  return { data, series };
};
